import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { TFunction, useTranslation } from 'react-i18next';
import {
	Button,
	Modal,
	Spin,
	Table,
	TablePaginationConfig,
	Tooltip,
} from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import OmniNotification from '../../../components/OmniNotification';
import { AppDispatch } from 'redux/store';
import { getCellValue, getShowTotal } from 'utils/tableUtils';
import { useHistory, useParams } from 'react-router-dom';
import styles from './TestUsersTab.module.less';
import TestUsersInvitationModal from '../TestUsersInvitationModal';
import {
	CollaboratorPermission,
	ProjectStatusDto,
	ReleaseChannel,
	TestUserDto,
	TestUserStatus,
} from '../../../api';
import {
	deleteTestUser,
	getTestUsers,
} from '../../../redux/reducers/testuserSlice';
import { DeleteOutlined } from '@ant-design/icons';
import { getProjectStatus } from '../../../redux/reducers/projectsSlice';
import { userSelector } from '../../../redux/selectors/userSelector';

type HandleChangeParams = {
	id: number;
	testUserName: string;
};

type ParamsProps = {
	projectId: string;
};

const getColumns = (t: TFunction, deleteModalOpen: Function) => [
	{
		title: t('playersPage.Tab.Users.Table.UserName'),
		key: 'testUserName',
		render: (record: TestUserDto) => (
			<>
				<span>
					{record.testUserName ? getCellValue(record.testUserName) : '-'}
				</span>
				<span style={{ fontStyle: 'italic', marginLeft: 5 }}>
					{`(${t(
						'TestUserStatus.' + TestUserStatus[getCellValue(record.status)],
					)})`}
				</span>
			</>
		),
	},
	{
		title: t('playersPage.Tab.Users.Table.UserEmail'),
		key: 'testUserEmail',
		render: (record: TestUserDto) => getCellValue(record.testUserEmail),
	},
	{
		title: t('playersPage.Tab.Users.Table.PermittedChannels'),
		key: 'premittedReleaseChannels',
		render: () => t('playersPage.Tab.Users.Table.BetaChannel'),
	},
	{
		title: t('playersPage.Tab.Users.Table.Delete'),
		key: 'delete',
		render: (record: TestUserDto) => (
			<DeleteOutlined
				onClick={() =>
					deleteModalOpen(
						record.id ?? 0,
						record.testUserName || record.testUserEmail,
					)
				}
			/>
		),
		width: 80,
		align: 'center' as const,
	},
];

export default function TestUsersTab() {
	const dispatch: AppDispatch = useDispatch();
	const { t } = useTranslation();
	const projectId = parseInt(useParams<ParamsProps>().projectId);
	const [inviteModalVisible, setInviteModalVisible] = useState(false);
	const [projectStatus, setProjectStatus] = useState<ProjectStatusDto>();
	const inviteModalOpen = useCallback(() => {
		setInviteModalVisible(true);
	}, [inviteModalVisible]);
	const showDeleteConfirmation = (id: number, testUserName: string) =>
		deleteModal(id, testUserName);
	const columns = useMemo(
		() => getColumns(t, showDeleteConfirmation),
		[t, showDeleteConfirmation],
	);
	const showTotal = useCallback(
		(total: number, range: [number, number]) => getShowTotal(total, range, t),
		[t],
	);
	const [tableData, setTableData] = useState<TestUserDto[]>([]);
	const [hasNoTestUser, setHasNoTestUser] = useState(false);
	const [pagination, setPagination] = useState<TablePaginationConfig>({
		current: 1,
		pageSize: 10,
		size: 'default',
		showQuickJumper: true,
		showTotal,
		showSizeChanger: true,
	});
	const [isLoading, setIsLoading] = useState(true);
	const [tableIsLoading, setTableIsLoading] = useState(false);
	const history = useHistory();
	const userData = useSelector(userSelector);

	const projectPermissions = userData?.projectPermissions?.find(
		(x) => x.projectId === projectId,
	)?.permissions;

	useEffect(() => {
		const getChanges = async () => {
			await handleTableChange({ ...pagination, current: 1 });
			await initProjectStatus();
		};
		getChanges();
	}, []);

	const initProjectStatus = async () => {
		setIsLoading(true);
		const result = await dispatch(getProjectStatus(projectId));

		if (getProjectStatus.fulfilled.match(result)) {
			setProjectStatus(result.payload);
			setIsLoading(false);
		}
	};

	const handleTableChange = async (tablePagination: TablePaginationConfig) => {
		setTableIsLoading(true);
		const result = await dispatch(
			getTestUsers({
				pageNum: tablePagination.current || 1,
				pageSize: tablePagination.pageSize as number,
				projectId,
			}),
		);
		setTableIsLoading(false);

		if (getTestUsers.fulfilled.match(result)) {
			setTableData(result.payload.items || []);
			setPagination({
				...pagination,
				...tablePagination,
				total: result.payload.totalCount || 0,
			});
		}

		setHasNoTestUser(
			!!(!(result.payload as any)?.totalCount && tablePagination.current),
		);
	};

	//delete TestUser
	const deleteModal = (id: number, testUserName: string) => {
		const deleteParams: HandleChangeParams = {
			id,
			testUserName,
		};
		Modal.confirm({
			icon: <InfoCircleOutlined style={{ color: 'red' }} />,
			title: (
				<span className={styles.warningModalHeader}>
					{t('playersPage.Tab.Users.DeleteTestUserModal.Header')}
				</span>
			),
			okText: t('playersPage.Tab.Users.DeleteTestUserModal.OkButton'),
			cancelText: t('common.cancel'),
			onOk() {
				return handleDeleteTestUser(deleteParams);
			},
			cancelButtonProps: {
				className: 'color-dark',
			},
			content: (
				<span>
					{t('playersPage.Tab.Users.DeleteTestUserModal.Body', {
						testUserName,
					})}
				</span>
			),
		});
	};

	const handleDeleteTestUser = async (data: HandleChangeParams) => {
		if (!data.id) return;
		try {
			await dispatch(deleteTestUser({ id: data.id, projectId }));

			OmniNotification.success({
				message: t('playersPage.Tab.Users.DeleteTestUserModal.Success.Header'),
				description: t(
					'playersPage.Tab.Users.DeleteTestUserModal.Success.Body',
					{ testUserName: data.testUserName },
				),
			});
		} catch (e) {
			OmniNotification.error({
				message: t('notification.error.title'),
				description: t('api.defaultError'),
			});
		} finally {
			await handleTableChange(pagination);
		}
	};

	//invite TestUser
	function handleOpenInvitationModal(reassignModal?: { destroy: () => void }) {
		reassignModal?.destroy();
		inviteModalOpen();
	}

	const renderTable = () => {
		if (hasNoTestUser) {
			if (projectStatus?.hasBetaBuild) {
				return (
					<div className={styles.noPlayers} data-testid="noPlayersDiv">
						<h2 className="h3">{t('playersPage.Tab.Users.None.Line1')}</h2>
						<br />
						<h2 className="h3">{t('playersPage.Tab.Users.None.Line2')}</h2>
					</div>
				);
			} else {
				if (projectPermissions?.includes(CollaboratorPermission.Builds)) {
					if (!projectStatus?.hasBetaBuild) {
						return (
							<div className={styles.noPlayers} data-testid="noBuildsDiv">
								<h2 className="h3">
									{t('playersPage.Tab.Users.NoBuilds.Line1')}
								</h2>
								<br />
								<h2 className="h3">
									{t('playersPage.Tab.Users.NoBuilds.Line2')}
								</h2>
								<br />
								<Button
									type="primary"
									data-testid="goToBuildsBtn"
									onClick={() => history.push(`/project/${projectId}/builds`)}
								>
									{t('playersPage.Tab.Users.NoBuilds.Button')}
								</Button>
							</div>
						);
					}
				} else {
					return (
						<div className={styles.noPlayers} data-testid="noAccessDiv">
							<h2 className="h3">
								{t('playersPage.Tab.Users.NoAccess.Line1')}
							</h2>
							<br />
							<br />
							<ol>
								<li className="h3">
									{t('playersPage.Tab.Users.NoAccess.Line2')}
								</li>
								<li className="h3">
									{t('playersPage.Tab.Users.NoAccess.Line3')}
								</li>
								<li className="h3">
									{t('playersPage.Tab.Users.NoAccess.Line4')}
								</li>
							</ol>
						</div>
					);
				}
			}
		} else {
			return (
				<Table
					className="table"
					size="middle"
					columns={columns}
					rowKey={(record) => record.id || ('' as string)}
					dataSource={tableData}
					pagination={pagination}
					loading={tableIsLoading}
					onChange={handleTableChange}
					tableLayout="fixed"
					data-testid="testUsersTable"
				/>
			);
		}
	};

	if (isLoading || projectStatus == undefined) return <Spin />;

	return (
		<>
			<div className="page-head">
				<div>
					<h3 className="h3">{t('playersPage.Tab.Users')}</h3>
				</div>
				<div>
					<Tooltip
						overlayStyle={{ maxWidth: '550px' }}
						title={
							!projectStatus?.hasBetaBuild
								? t('playersPage.toolTip.disabledInviteButton')
								: ''
						}
						placement="left"
					>
						<Button
							type="primary"
							data-testid="sendInvitationBtn"
							disabled={!projectStatus?.hasBetaBuild}
							onClick={() => handleOpenInvitationModal()}
						>
							{t('playersPage.Tab.Users.Header.InvitePlayer')}
						</Button>
					</Tooltip>
				</div>
			</div>
			{renderTable()}
			<TestUsersInvitationModal
				visible={inviteModalVisible}
				setVisible={(visible) => setInviteModalVisible(visible)}
				afterSubmit={async () => {
					setInviteModalVisible(false);
					await handleTableChange(pagination);
				}}
				projectId={projectId}
			/>
		</>
	);
}
